(function pressdStorefront() {
  "use strict";

  const store = window.PRESSD_STORE;

  if (!store || !Array.isArray(store.products) || !Array.isArray(store.kit)) {
    console.error("Pressd Nails storefront data is unavailable.");
    return;
  }

  const { products, kit, settings } = store;
  const reducedMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
  const finePointerQuery = window.matchMedia("(pointer: fine)");
  const HERO_INTERVAL = Number(settings.heroIntervalMs) || 6000;
  const SPOTLIGHT_SESSION_KEY = "pressd-nails-spotlight-v1";
  const productById = new Map(products.map((product) => [product.id, product]));
  const productBySlug = new Map(products.map((product) => [product.slug, product]));
  const observedReveals = new WeakSet();
  const magnetizedElements = new WeakSet();
  const SIZE_MEASUREMENTS = {
    regular_xs: [["Thumb", "3", "19.7", "14"], ["Index", "8", "17", "10"], ["Middle", "7", "17.5", "11"], ["Ring", "8", "17", "10"], ["Pinky", "11", "14.2", "8"]],
    regular_s: [["Thumb", "2", "20", "15"], ["Index", "7", "17.5", "11"], ["Middle", "6", "18.4", "12"], ["Ring", "7", "17.5", "11"], ["Pinky", "10", "15", "9"]],
    regular_m: [["Thumb", "1", "20.3", "16"], ["Index", "6", "18.4", "12"], ["Middle", "5", "19", "13"], ["Ring", "6", "18.4", "12"], ["Pinky", "9", "16", "10"]],
    regular_l: [["Thumb", "0", "20.6", "17"], ["Index", "5", "19", "13"], ["Middle", "4", "19.5", "14"], ["Ring", "5", "19", "13"], ["Pinky", "8", "17", "10"]],
    navratri_s: [["Thumb", "2", "20", "12"], ["Index", "7", "19", "10"], ["Middle", "5", "20", "11.9"], ["Ring", "6", "19", "10.5"], ["Pinky", "11", "17", "8.5"]],
    navratri_m: [["Thumb", "2", "30", "13"], ["Index", "6", "26", "9"], ["Middle", "8", "24", "8"], ["Ring", "7", "25", "9"], ["Pinky", "9", "23", "8"]],
    medium_almond_xs: [["Thumb", "3", "22", "10"], ["Index", "8", "19", "7"], ["Middle", "7", "19.5", "7.5"], ["Ring", "8", "19", "7"], ["Pinky", "9", "18", "6.5"]],
    medium_almond_s: [["Thumb", "2", "23", "11.5"], ["Index", "7", "19.5", "7.5"], ["Middle", "6", "20", "8.5"], ["Ring", "7", "19.5", "7.5"], ["Pinky", "9", "18", "6.5"]],
    medium_almond_m: [["Thumb", "1", "23.5", "12.5"], ["Index", "6", "20", "8.5"], ["Middle", "5", "20.5", "9"], ["Ring", "6", "20", "8.5"], ["Pinky", "9", "18", "6.5"]],
    medium_almond_l: [["Thumb", "0", "24", "13.5"], ["Index", "5", "20.5", "9"], ["Middle", "4", "21", "9.5"], ["Ring", "5", "20.5", "9"], ["Pinky", "8", "19", "7"]],
  };

  const state = {
    filters: { mood: "", color: "", finish: "" },
    heroIndex: 0,
    heroTimer: 0,
    heroTransitionTimer: 0,
    currentProduct: null,
    galleryIndex: 0,
    cart: { items: [], item_count: 0, total_price: 0, currency: settings.currency },
    cartPending: false,
    cartError: "",
    revealObserver: null,
    stickyAtcObserver: null,
    stickyAtcScrollHandler: null,
    quickAddProduct: null,
    quickAddSelections: [],
    sizeGuideVariantId: "",
  };

  const ritualSteps = [
    {
      title: "Buff",
      copy: "Lightly buff the nail surface so the adhesive has a clean place to meet.",
    },
    {
      title: "Cleanse",
      copy: "Remove dust and surface oils, then let every nail dry completely.",
    },
    {
      title: "Apply",
      copy: "Choose each size before you begin, then follow the directions for your adhesive.",
    },
    {
      title: "Press",
      copy: "Align carefully and hold each nail in place for 10 seconds.",
    },
    {
      title: "Shape",
      copy: "File the edge to your preferred length and shape if needed.",
    },
  ];

  function escapeHTML(value) {
    return String(value ?? "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;");
  }

  function formatMoney(cents) {
    try {
      return new Intl.NumberFormat(settings.locale, {
        style: "currency",
        currency: settings.currency,
      }).format(cents / 100);
    } catch (_error) {
      return `${(cents / 100).toFixed(2)}`;
    }
  }

  function productHref(product) {
    const shopifyUrl = window.PRESSD_SHOPIFY_CONTEXT?.productUrls?.[product.slug];
    if (shopifyUrl) return shopifyUrl;
    return `${window.PRESSD_SHOPIFY_CONTEXT?.routes?.root || "/"}products/${encodeURIComponent(product.slug)}`;
  }

  function getProduct(reference) {
    if (!reference) return null;
    return productById.get(reference) || productBySlug.get(reference) || null;
  }

  function optionValueLabel(value) {
    if (typeof value === "string" || typeof value === "number") return String(value);
    return String(value?.name || value?.value || "");
  }

  function productChoiceOptions(product) {
    return (product?.options || []).filter(
      (option) => String(option?.name || "").toLowerCase() !== "title",
    );
  }

  function variantMatchesSelections(variant, selections, exceptIndex = -1) {
    return selections.every(
      (value, index) =>
        index === exceptIndex || !value || String(variant.options?.[index] || "") === value,
    );
  }

  function selectedQuickAddVariant(product, selections = state.quickAddSelections) {
    const choiceOptions = productChoiceOptions(product);
    const choicesComplete = choiceOptions.every((option) => {
      const optionIndex = Math.max(0, Number(option.position || 1) - 1);
      return Boolean(selections[optionIndex]);
    });
    if (!choicesComplete) return null;
    return (product.variants || []).find(
      (variant) => variant.available && variantMatchesSelections(variant, selections),
    ) || null;
  }

  function quickAddOptionAvailable(product, optionIndex, value) {
    const testSelections = [...state.quickAddSelections];
    testSelections[optionIndex] = value;
    return (product.variants || []).some(
      (variant) =>
        variant.available && variantMatchesSelections(variant, testSelections, optionIndex) &&
        String(variant.options?.[optionIndex] || "") === value,
    );
  }

  function ensureQuickAddDialog() {
    let dialog = document.querySelector("[data-quick-add-dialog]");
    if (dialog) return dialog;
    dialog = document.createElement("dialog");
    dialog.id = "quick-add-dialog";
    dialog.className = "quick-add-sheet";
    dialog.dataset.quickAddDialog = "";
    dialog.setAttribute("aria-labelledby", "quick-add-title");
    document.body.append(dialog);
    bindDialogLifecycle(dialog);
    return dialog;
  }

  function syncQuickAddSheet() {
    const dialog = document.querySelector("[data-quick-add-dialog]");
    const product = state.quickAddProduct;
    if (!dialog || !product) return;

    dialog.querySelectorAll("[data-quick-add-option]").forEach((input) => {
      const optionIndex = Number(input.dataset.quickAddOption);
      input.disabled = !quickAddOptionAvailable(product, optionIndex, input.value);
    });

    const choiceOptions = productChoiceOptions(product);
    const missing = choiceOptions
      .filter((option) => {
        const optionIndex = Math.max(0, Number(option.position || 1) - 1);
        return !state.quickAddSelections[optionIndex];
      })
      .map((option) => option.name);
    const variant = selectedQuickAddVariant(product);
    const submit = dialog.querySelector("[data-quick-add-submit]");
    const status = dialog.querySelector("[data-quick-add-status]");
    const sku = dialog.querySelector("[data-quick-add-sku]");

    if (submit) {
      submit.disabled = !variant;
      submit.dataset.variantId = variant?.id || "";
      submit.textContent = variant
        ? `Add to bag · ${formatMoney(variant.priceCents)}`
        : missing.length
          ? `Choose ${missing.join(" + ")}`
          : "This combination is unavailable";
    }
    if (status) {
      status.textContent = variant
        ? `${variant.title === "Default Title" ? product.name : variant.title} is ready to add.`
        : missing.length
          ? `Choose ${missing.join(" and ")} to continue.`
          : "That combination is currently unavailable.";
    }
    if (sku) {
      sku.textContent = variant?.sku ? `SKU ${variant.sku}` : "";
    }
  }

  function renderQuickAddSheet(product) {
    const dialog = ensureQuickAddDialog();
    const choiceOptions = productChoiceOptions(product);
    const selectionLength = Math.max(1, ...(product.options || []).map((option) => Number(option.position) || 1));
    state.quickAddProduct = product;
    state.quickAddSelections = new Array(selectionLength).fill("");

    const optionGroups = choiceOptions.map((option) => {
      const optionIndex = Math.max(0, Number(option.position || 1) - 1);
      const values = (option.values || [])
        .map(optionValueLabel)
        .filter(Boolean)
        .map((value, valueIndex) => {
          const inputId = `quick-add-${product.slug}-${optionIndex}-${valueIndex}`;
          const globallyAvailable = (product.variants || []).some(
            (variant) =>
              variant.available && String(variant.options?.[optionIndex] || "") === value,
          );
          return `<label class="quick-add-sheet__choice" for="${escapeHTML(inputId)}">
            <input id="${escapeHTML(inputId)}" type="radio" name="quick-add-option-${optionIndex}" value="${escapeHTML(value)}" data-quick-add-option="${optionIndex}"${globallyAvailable ? "" : " disabled"}>
            <span>${escapeHTML(value)}</span>
          </label>`;
        })
        .join("");
      return `<fieldset class="quick-add-sheet__options" data-quick-add-option-group="${optionIndex}">
        <legend>${escapeHTML(option.name)}</legend>
        <div class="quick-add-sheet__choices">${values}</div>
      </fieldset>`;
    }).join("");
    const media = primaryMedia(product);

    dialog.innerHTML = `<div class="quick-add-sheet__panel">
      <header class="quick-add-sheet__header">
        <div><span class="eyebrow">Quick add</span><h2 id="quick-add-title">${escapeHTML(product.name)}</h2></div>
        <button class="dialog-close" type="button" data-dialog-close aria-label="Close quick add">×</button>
      </header>
      <div class="quick-add-sheet__product">
        ${mediaMarkup(media, {
          className: "quick-add-sheet__image",
          loading: "eager",
          sizes: "96px",
        })}
        <div><strong>${escapeHTML(formatMoney(product.priceCents))}</strong><p>${escapeHTML(product.worldTagline)}</p></div>
      </div>
      <form class="quick-add-sheet__form" data-quick-add-form data-product-handle="${escapeHTML(product.slug)}">
        ${optionGroups}
        <div class="quick-add-sheet__fit-help">
          <div><strong>Not sure which size?</strong><span>Measure the widest part of each natural nail in millimetres.</span></div>
          <button type="button" data-open-size-guide>Find my size</button>
        </div>
        <p class="quick-add-sheet__status" data-quick-add-status role="status" aria-live="polite"></p>
        <p class="quick-add-sheet__sku" data-quick-add-sku></p>
        <button class="button quick-add-sheet__submit" type="submit" data-quick-add-submit disabled>Choose options</button>
      </form>
      <a class="quick-add-sheet__details" href="${productHref(product)}">View full product details</a>
    </div>`;
    syncQuickAddSheet();
    return dialog;
  }

  function openQuickAdd(reference) {
    const product = getProduct(reference);
    if (!product?.isRevealed || !product?.canAddToCart || !(product.variants || []).some((variant) => variant.available)) {
      return;
    }
    openDialog(renderQuickAddSheet(product));
  }

  function primaryMedia(product) {
    return product.gallery.find((media) => !media.revealPlaceholder) || product.gallery[0];
  }

  function roleMedia(product, field) {
    const index = Number(product?.[field]);
    const media = Number.isInteger(index) ? product?.gallery?.[index] : null;
    return media?.src && !media.revealPlaceholder ? media : primaryMedia(product);
  }

  function homepageHeroMedia(product) {
    if (!product) return null;
    return product?.homepageHeroMedia?.src
      ? product.homepageHeroMedia
      : roleMedia(product, "heroMediaIndex");
  }

  function mediaMarkup(media, options = {}) {
    const {
      className = "",
      loading = "lazy",
      decorative = false,
      sizes = "(max-width: 720px) 100vw, 50vw",
      decoding = "async",
      fetchPriority = "",
    } = options;

    if (!media || !media.src || media.revealPlaceholder) {
      const label = media?.alt || "Product image unavailable";
      return `<div class="media-placeholder ${className}" role="img" aria-label="${escapeHTML(
        label,
      )}" data-media-placeholder><span>Pressd Nails</span><strong>Image unavailable</strong></div>`;
    }

    return `<img class="${className}" src="${escapeHTML(media.src)}" alt="${
      decorative ? "" : escapeHTML(media.alt)
    }" width="${Number(media.width)}" height="${Number(
      media.height,
    )}" loading="${loading}" decoding="${escapeHTML(decoding)}" sizes="${escapeHTML(
      sizes,
    )}"${fetchPriority ? ` fetchpriority="${escapeHTML(fetchPriority)}"` : ""} data-media-kind="${escapeHTML(media.kind)}" data-product-photo="${
      media.isProductPhotography ? "true" : "false"
    }"${decorative ? ' aria-hidden="true"' : ""}>`;
  }

  function setProductTheme(product) {
    const root = document.documentElement;
    root.style.setProperty("--accent", product.accentColor);
    root.style.setProperty("--accent-alt", product.secondaryColor);
    root.style.setProperty("--product-soft", product.softColor);
    const hero = document.querySelector(".hero");
    if (hero) {
      hero.style.setProperty("--accent", product.accentColor);
      hero.style.setProperty("--accent-alt", product.secondaryColor);
      hero.style.setProperty("--product-soft", product.softColor);
    }
  }

  function preloadMedia(media) {
    if (!media?.src) return;
    const selector = `link[rel="preload"][as="image"][href="${CSS.escape(media.src)}"]`;
    if (!document.head.querySelector(selector)) {
      const preload = document.createElement("link");
      preload.rel = "preload";
      preload.as = "image";
      preload.href = media.src;
      preload.fetchPriority = "high";
      document.head.append(preload);
    }
    const image = new Image();
    image.decoding = "async";
    image.src = media.src;
    image.decode?.().catch(() => {});
  }

  function renderStoreTokens() {
    document.querySelectorAll("[data-brand-name]").forEach((element) => {
      element.textContent = settings.brandName;
    });
    document.querySelectorAll("[data-brand-tagline]").forEach((element) => {
      element.textContent = settings.tagline;
    });
    document.querySelectorAll("[data-product-count]").forEach((element) => {
      element.textContent = String(products.length);
    });
    document.querySelectorAll("[data-social-handle]").forEach((element) => {
      element.textContent = settings.socialHandle;
    });
    document.querySelectorAll("[data-reviews-message]").forEach((element) => {
      element.textContent = settings.reviewsMessage;
    });
    document.querySelectorAll("[data-kit-summary]").forEach((element) => {
      element.textContent = formatList(
        kit.map((item) => item.name.toLocaleLowerCase(settings.locale)),
      );
    });
    document.querySelectorAll("[data-current-year]").forEach((element) => {
      element.textContent = String(new Date().getFullYear());
    });
    document.querySelectorAll("[data-prop65-warning]").forEach((element) => {
      const value = String(settings.compliance.prop65 || "").trim();
      element.textContent = value;
      element.closest("[data-compliance-item]")?.toggleAttribute("hidden", !value);
    });
    document
      .querySelectorAll("[data-ingredients-declaration]")
      .forEach((element) => {
        const value = String(settings.compliance.ingredients || "").trim();
        element.textContent = value;
        element.closest("[data-compliance-item]")?.toggleAttribute("hidden", !value);
      });
    document.querySelectorAll("[data-checkout]").forEach((element) => {
      element.textContent = settings.checkoutMessage;
    });
  }

  function renderAnnouncement() {
    document.querySelectorAll("[data-announcement]").forEach((container) => {
      container.innerHTML = settings.announcementMessages
        .map(
          (message, index) => {
            const mobileCopy =
              index === 0
                ? settings.mobileAnnouncementMessage || "Shipping's on us—no minimum"
                : message;
            return `<span class="announcement__message"><span class="announcement__wide">${escapeHTML(
              message,
            )}</span><span class="announcement__compact">${escapeHTML(
              mobileCopy,
            )}</span></span>${
              index < settings.announcementMessages.length - 1
                ? '<i aria-hidden="true">✦</i>'
                : ""
            }`;
          },
        )
        .join("");
    });
  }

  function renderProductNav() {
    document.querySelectorAll("[data-product-nav]").forEach((container) => {
      container.innerHTML = products
        .map(
          (product) =>
            `<a href="${productHref(product)}" style="--item-accent:${escapeHTML(
              product.accentColor,
            )}"><span>${escapeHTML(product.catalogCode)}</span>${escapeHTML(
              product.name,
            )}</a>`,
        )
        .join("");
    });
  }

  function renderMarquees() {
    const phrases = Array.isArray(settings.moodMarquee)
      ? settings.moodMarquee
      : [];
    const moodGroup = phrases
      .map(
        (phrase, index) =>
          `<span class="mood-strip__phrase${index % 2 ? " is-holo" : ""}">${escapeHTML(
            phrase,
          )}</span><i aria-hidden="true">✦</i>`,
      )
      .join("");
    document.querySelectorAll("[data-mood-marquee]").forEach((container) => {
      container.innerHTML = `<div class="mood-strip__track"><div class="mood-strip__group">${moodGroup}</div><div class="mood-strip__group" aria-hidden="true">${moodGroup}</div></div>`;
    });

    const productGroup = products
      .map(
        (product) =>
          `<a href="${productHref(product)}" style="--item-accent:${escapeHTML(
            product.accentColor,
          )}"><span>${escapeHTML(product.catalogCode)}</span>${escapeHTML(
            product.name,
          )}</a><i aria-hidden="true">✦</i>`,
      )
      .join("");
    document.querySelectorAll("[data-product-marquee]").forEach((container) => {
      container.innerHTML = `<div class="product-marquee__track"><div class="product-marquee__group">${productGroup}</div><div class="product-marquee__group" aria-hidden="true">${productGroup}</div></div>`;
    });
  }

  function uniqueTagValues(key) {
    return [
      ...new Set(products.flatMap((product) => product.tags[key] || [])),
    ].sort((a, b) => a.localeCompare(b));
  }

  function humanizeTag(value) {
    return value
      .split("-")
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ");
  }

  function formatList(values) {
    try {
      return new Intl.ListFormat(settings.locale, {
        style: "long",
        type: "conjunction",
      }).format(values);
    } catch (_error) {
      if (values.length < 2) return values[0] || "";
      return `${values.slice(0, -1).join(", ")} and ${values.at(-1)}`;
    }
  }

  function renderFilters() {
    const filterKeys = ["mood", "color", "finish"];
    document.querySelectorAll("[data-filter-bar]").forEach((container, barIndex) => {
      const selects = filterKeys
        .map((key) => {
          const options = uniqueTagValues(key)
            .map(
              (value) =>
                `<option value="${escapeHTML(value)}"${
                  state.filters[key] === value ? " selected" : ""
                }>${escapeHTML(humanizeTag(value))}</option>`,
            )
            .join("");
          const id = `catalog-filter-${key}-${barIndex}`;
          return `<label class="visually-hidden" for="${id}">Filter by ${escapeHTML(
            key,
          )}</label><select class="filter-chip filter-chip--${escapeHTML(key)}" id="${id}" data-filter-key="${key}"><option value="">${escapeHTML(
            humanizeTag(key),
          )} · All</option>${options}</select>`;
        })
        .join("");
      const hasFilter = Object.values(state.filters).some(Boolean);
      container.innerHTML = `<div class="filter-list"><button class="filter-reset filter-chip${
        hasFilter ? "" : " is-active"
      }" type="button" data-filter-reset aria-pressed="${String(
        !hasFilter,
      )}">All worlds</button>${selects}</div><span class="filter-result collection-count" data-filter-result aria-live="polite"></span>`;
    });
  }

  function filterProducts(filterState = state.filters, source = products) {
    return source.filter((product) =>
      Object.entries(filterState).every(
        ([key, value]) => !value || (product.tags[key] || []).includes(value),
      ),
    );
  }

  function productCardMarkup(product, index = 0) {
    const primary = primaryMedia(product);
    const secondary = product.isRevealed
      ? product.gallery.find(
          (media) =>
            media !== primary && media.src && !media.revealPlaceholder,
        )
      : null;
    const mediaLabel = product.isRevealed
      ? "Real product photography"
      : "Product image unavailable";
    const badgeLabel = product.isRevealed ? product.badge : "Image unavailable";
    const requiresOptions = (product.options || []).some(
      (option) => option.name !== "Title" && (option.values || []).length > 1,
    );
    const quickAction = product.isRevealed && product.canAddToCart && !requiresOptions
      ? `<button class="product-card__quick" type="button" data-add-to-cart="${escapeHTML(
          product.selectedVariantId,
        )}" aria-label="Quick add ${escapeHTML(product.name)}">Quick add <span aria-hidden="true">+</span></button>`
      : product.isRevealed && product.canAddToCart
        ? `<button class="product-card__quick" type="button" data-quick-add-open="${escapeHTML(product.slug)}" aria-haspopup="dialog" aria-controls="quick-add-dialog" aria-label="Choose a size and quick add ${escapeHTML(product.name)}">Choose size <span aria-hidden="true">+</span></button>`
      : `<a class="product-card__quick product-card__notify" href="#newsletter" aria-label="Join the list for ${escapeHTML(
          product.name,
        )}">Notify me <span aria-hidden="true">↗</span></a>`;
    const mobileQuickAction = `<button class="product-card__mobile-quick" type="button" data-quick-add-open="${escapeHTML(
      product.slug,
    )}" aria-haspopup="dialog" aria-controls="quick-add-dialog" aria-label="${
      product.isRevealed && product.canAddToCart
        ? `Choose options and quick add ${escapeHTML(product.name)}`
        : `${escapeHTML(product.name)} is currently unavailable`
    }"${product.isRevealed && product.canAddToCart ? "" : ' disabled aria-disabled="true"'}><span aria-hidden="true">+</span></button>`;
    const action = product.isRevealed && product.canAddToCart && !requiresOptions
      ? `<button class="button button--outline product-card__atc" type="button" data-add-to-cart="${escapeHTML(
          product.selectedVariantId,
        )}" data-magnetic>Add to bag <span>${escapeHTML(
          formatMoney(product.priceCents),
        )}</span></button>`
      : product.isRevealed && product.canAddToCart
        ? `<button class="button button--outline product-card__atc" type="button" data-quick-add-open="${escapeHTML(product.slug)}" aria-haspopup="dialog" aria-controls="quick-add-dialog">Choose size <span aria-hidden="true">+</span></button>`
      : `<a class="button button--outline product-card__atc" href="${productHref(product)}">View set <span aria-hidden="true">↗</span></a>`;
    const rating = Number(product.reviewRating) || 0;
    const ratingCount = Number(product.reviewCount) || 0;
    const ratingScale = Math.max(1, Number(product.reviewScaleMax) || 5);
    const ratingFill = Math.min(100, Math.max(0, rating / ratingScale * 100));
    const ratingMarkup = rating > 0 && ratingCount > 0
      ? `<p class="product-card__rating" aria-label="${escapeHTML(rating)} out of ${escapeHTML(ratingScale)} stars from ${escapeHTML(ratingCount)} reviews"><span class="pressd-review-stars" style="--rating-fill:${ratingFill}%" aria-hidden="true"><span>★★★★★</span><span>★★★★★</span></span><strong>${escapeHTML(rating)}</strong><small>(${escapeHTML(ratingCount)})</small></p>`
      : "";

    return `<article class="product-card${
      product.isRevealed ? "" : " product-card--unrevealed"
    }" style="--card-accent:${escapeHTML(
      product.accentColor,
    )};--card-accent-alt:${escapeHTML(
      product.secondaryColor,
    )};--card-soft:${escapeHTML(product.softColor)};--reveal-order:${index}" data-product-card="${escapeHTML(
      product.id,
    )}" data-reveal>
      <div class="product-card__visual">
        <a class="product-card__media-link" href="${productHref(
          product,
        )}" aria-label="View ${escapeHTML(product.name)}" tabindex="-1">
          <div class="product-card__media${
          product.isRevealed ? "" : " product-card__world-art"
        }">
            <span class="product-card__badge">${escapeHTML(badgeLabel)}</span>
            ${mediaMarkup(primary, {
              className: "product-card__image product-card__image--primary",
              loading: "lazy",
              sizes: "(max-width: 819px) calc(50vw - 1.25rem), (max-width: 1100px) 46vw, 31vw",
            })}
            ${
              secondary
                ? mediaMarkup(secondary, {
                    className: "product-card__image product-card__image--secondary",
                    loading: "lazy",
                    sizes: "(max-width: 819px) calc(50vw - 1.25rem), (max-width: 1100px) 46vw, 31vw",
                  })
                : ""
            }
            <span class="product-card__status">${escapeHTML(mediaLabel)}</span>
          </div>
        </a>
        ${quickAction}
        ${mobileQuickAction}
      </div>
      <a class="product-card__link product-card__details-link" href="${productHref(
        product,
      )}" aria-label="View details for ${escapeHTML(product.name)}">
        <div class="product-card__meta">
          <div><span class="eyebrow">${escapeHTML(
            product.catalogCode,
          )}</span><h3 class="product-card__name">${escapeHTML(
            product.name,
          )}</h3></div>
          <strong class="product-card__price">${escapeHTML(
            formatMoney(product.priceCents),
          )}</strong>
          <p class="product-card__tagline">${escapeHTML(product.worldTagline)}</p>
          ${ratingMarkup}
        </div>
      </a>
      ${action}
    </article>`;
  }

  function renderCollection() {
    const filtered = filterProducts();
    document.querySelectorAll("[data-product-grid]").forEach((container) => {
      container.innerHTML = filtered.length
        ? filtered.map(productCardMarkup).join("")
        : '<div class="catalog-empty" role="status"><strong>No worlds match yet.</strong><button type="button" data-filter-reset>Clear the filters</button></div>';
    });
    document.querySelectorAll("[data-filter-result]").forEach((element) => {
      element.textContent = `${filtered.length} ${filtered.length === 1 ? "world" : "worlds"}`;
    });
    setupRevealElements();
    setupMagneticElements();
  }

  function heroProducts() {
    return products
      .filter((product) => product.homepageHeroMedia?.src && product.slug !== "malibu-mood")
      .slice(0, 6);
  }

  function renderHero() {
    const eligible = heroProducts();
    if (!eligible.length) return;
    state.heroIndex =
      ((state.heroIndex % eligible.length) + eligible.length) % eligible.length;
    const product = eligible[state.heroIndex];
    const media = homepageHeroMedia(product);
    const campaign = product.homepageHeroCampaign;
    const dots = eligible
      .map(
        (item, index) =>
          `<button class="hero__dot" type="button" data-hero-index="${index}" aria-label="Show ${escapeHTML(
            item.name,
          )}" aria-pressed="${String(index === state.heroIndex)}"><span aria-hidden="true"></span></button>`,
      )
      .join("");

    if (document.querySelector("[data-hero]")) setProductTheme(product);
    document.querySelectorAll("[data-hero]").forEach((container) => {
      container.dataset.heroProduct = product.id;
      container.classList.toggle("has-campaign-hero", Boolean(campaign));
      container.classList.toggle(
        "has-bottom-campaign",
        campaign?.placement === "bottom-bar",
      );
      container.classList.toggle(
        "has-right-campaign",
        campaign?.placement === "right-editorial",
      );
      container.style.setProperty("--accent", product.accentColor);
      container.style.setProperty("--accent-alt", product.secondaryColor);
      container.style.setProperty("--product-soft", product.softColor);
      container.style.setProperty(
        "--hero-object-position",
        product.heroObjectPosition || "50% 50%",
      );
      const campaignMarkup = `<div class="hero-carousel__minimal">
        <h1>${escapeHTML(product.name)}</h1>
        <a href="${productHref(product)}">Shop the set <span aria-hidden="true">↗</span></a>
      </div>`;
      container.innerHTML = `<div class="hero-carousel section-shell">
        <div class="hero-carousel__stage hero-carousel__stage--minimal">
          <a class="hero-carousel__media" href="${productHref(
            product,
          )}" aria-label="Shop ${escapeHTML(product.name)}">
            ${mediaMarkup(media, {
              className: "hero-carousel__image",
              loading: "eager",
              decoding: "sync",
              fetchPriority: "high",
              sizes: "(max-width: 800px) calc(100vw - 2rem), min(92vw, 90rem)",
            })}
          </a>
          ${campaignMarkup}
        </div>
        ${
          eligible.length > 1
            ? `<div class="hero__selector" aria-label="Choose a product slide"><button type="button" data-hero-control="previous" aria-label="Previous product">←</button><div class="hero__dots">${dots}</div><button type="button" data-hero-control="next" aria-label="Next product">→</button></div>`
            : ""
        }
      </div>`;
    });
    const nextProduct = eligible[(state.heroIndex + 1) % eligible.length];
    preloadMedia(homepageHeroMedia(nextProduct));
  }

  function switchHero(nextIndex, focusSelector = "") {
    const eligible = heroProducts();
    if (!eligible.length) return;
    const normalized = (nextIndex + eligible.length) % eligible.length;
    const nextProduct = eligible[normalized];
    const containers = [...document.querySelectorAll("[data-hero]")];
    window.clearTimeout(state.heroTransitionTimer);
    preloadMedia(homepageHeroMedia(nextProduct));
    setProductTheme(nextProduct);

    const commit = () => {
      state.heroIndex = normalized;
      renderHero();
      window.requestAnimationFrame(() => {
        document
          .querySelectorAll("[data-hero]")
          .forEach((container) => container.classList.remove("is-switching"));
        if (focusSelector) document.querySelector(focusSelector)?.focus();
      });
    };

    if (reducedMotionQuery.matches || !containers.length) {
      commit();
      return;
    }

    containers.forEach((container) => container.classList.add("is-switching"));
    state.heroTransitionTimer = window.setTimeout(commit, 140);
  }

  function moveHero(delta, focusSelector = "") {
    const eligible = heroProducts();
    if (!eligible.length) return;
    switchHero(state.heroIndex + delta, focusSelector);
    startHeroRotation();
  }

  function initHeroSwipe(root = document) {
    root.querySelectorAll("[data-hero]").forEach((hero) => {
      if (hero.dataset.swipeBound === "true") return;
      hero.dataset.swipeBound = "true";
      let pointerId = null;
      let startX = 0;
      let startY = 0;
      let startTime = 0;
      let suppressClick = false;

      hero.addEventListener("keydown", (event) => {
        if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
        if (event.target.matches("input, select, textarea, [contenteditable='true']")) return;
        event.preventDefault();
        moveHero(event.key === "ArrowRight" ? 1 : -1);
      });

      hero.addEventListener("pointerdown", (event) => {
        if (event.pointerType === "mouse" || pointerId !== null) return;
        pointerId = event.pointerId;
        startX = event.clientX;
        startY = event.clientY;
        startTime = performance.now();
        stopHeroRotation();
        try {
          hero.setPointerCapture(event.pointerId);
        } catch (_error) {
          pointerId = event.pointerId;
        }
      });

      hero.addEventListener("pointerup", (event) => {
        if (event.pointerId !== pointerId) return;
        const deltaX = event.clientX - startX;
        const deltaY = event.clientY - startY;
        const elapsed = performance.now() - startTime;
        pointerId = null;
        if (
          Math.abs(deltaX) >= 48 &&
          Math.abs(deltaX) > Math.abs(deltaY) * 1.2 &&
          elapsed <= 900
        ) {
          suppressClick = true;
          moveHero(deltaX < 0 ? 1 : -1);
          window.setTimeout(() => {
            suppressClick = false;
          }, 450);
          return;
        }
        startHeroRotation();
      });

      hero.addEventListener("pointercancel", () => {
        pointerId = null;
        startHeroRotation();
      });

      hero.addEventListener(
        "click",
        (event) => {
          if (!suppressClick) return;
          event.preventDefault();
          event.stopPropagation();
          suppressClick = false;
        },
        true,
      );
    });
  }

  function startHeroRotation() {
    window.clearInterval(state.heroTimer);
    state.heroTimer = 0;
    const hero = document.querySelector("[data-hero]");
    if (!hero) return;
    const interactionActive = Boolean(
      hero.contains(document.activeElement) ||
        (finePointerQuery.matches && hero.matches(":hover")),
    );
    if (
      reducedMotionQuery.matches ||
      document.hidden ||
      interactionActive ||
      heroProducts().length < 2
    ) {
      return;
    }
    state.heroTimer = window.setInterval(() => moveHero(1), HERO_INTERVAL);
  }

  function stopHeroRotation() {
    window.clearInterval(state.heroTimer);
    state.heroTimer = 0;
  }

  function renderKit() {
    const visual = settings.kitVisual || {};
    document.querySelectorAll("[data-kit-stage]").forEach((container) => {
      const cards = kit
        .map((item, index) => {
          const side = item.side === "right" ? "right" : "left";
          const row = side === "left" ? index + 1 : index - 2;
          return `<li class="kit-item kit-item--${side}" data-reveal style="--kit-row:${row};--reveal-order:${index}">
            <div class="kit-item__top"><span class="kit-item__icon" aria-hidden="true">${escapeHTML(
              item.glyph || "✦",
            )}</span><span class="kit-item__number">${String(index + 1).padStart(
              2,
              "0",
            )}</span></div>
            <h3>${escapeHTML(item.name)}</h3>
            <p>${escapeHTML(item.detail)}</p>
          </li>`;
        })
        .join("");
      container.innerHTML = `<div class="kit-diagram">
        <figure class="kit-visual">
          <div class="kit-visual__photo">
            ${mediaMarkup(visual.primary, {
              className: "kit-visual__main-image",
              loading: "lazy",
              sizes: "(max-width: 820px) 92vw, 38vw",
            })}
            ${visual.guide?.src ? `<div class="kit-visual__guide" aria-hidden="true">${mediaMarkup(
              visual.guide,
              {
                className: "kit-visual__guide-image",
                loading: "lazy",
                decorative: true,
                sizes: "(max-width: 820px) 34vw, 12rem",
              },
            )}</div>` : ""}
          </div>
          <figcaption>${escapeHTML(
            visual.caption || "Real Pressd Nails set with illustrated kit callouts.",
          )}</figcaption>
        </figure>
        <ol class="kit__grid" data-kit-grid aria-label="Items included in every box">${cards}</ol>
      </div>`;
    });
    setupRevealElements();
  }

  function renderRealImageRail() {
    const realMedia = products
      .filter((product) => product.isRevealed)
      .map((product) => ({
        product,
        media: product.gallery.find(
          (item) =>
            item.isProductPhotography && item.src && !item.revealPlaceholder,
        ),
      }))
      .filter(({ media }) => Boolean(media));
    document.querySelectorAll("[data-real-image-rail]").forEach((container) => {
      container.innerHTML = realMedia
        .slice(0, 3)
        .map(({ product, media }) =>
          mediaMarkup(media, {
            className: "promise__image",
            loading: "lazy",
            sizes: "(max-width: 800px) 46vw, 28vw",
          }).replace(
            "<img ",
            `<img data-product-id="${escapeHTML(product.id)}" `,
          ),
        )
        .join("");
    });
  }

  function chooseSpotlight(_timestamp = Date.now(), source = products) {
    const eligible = source.filter(
      (product) => product.spotlightEligible && product.isRevealed,
    );
    if (!eligible.length) return null;
    if (source !== products) return eligible[0];
    try {
      const saved = window.sessionStorage.getItem(SPOTLIGHT_SESSION_KEY);
      const existing = eligible.find((product) => product.slug === saved);
      if (existing) return existing;
      const random = new Uint32Array(1);
      window.crypto?.getRandomValues?.(random);
      const index = random[0]
        ? random[0] % eligible.length
        : Math.floor(Math.random() * eligible.length);
      const product = eligible[index];
      window.sessionStorage.setItem(SPOTLIGHT_SESSION_KEY, product.slug);
      return product;
    } catch (_error) {
      return eligible[Math.floor(Math.random() * eligible.length)];
    }
  }

  function renderSpotlight() {
    const product = chooseSpotlight();
    document.querySelectorAll("[data-menu-featured]").forEach((link) => {
      if (!product) {
        link.hidden = true;
        return;
      }
      link.hidden = false;
      link.href = productHref(product);
      link.style.setProperty("--item-accent", product.accentColor);
      link.style.setProperty("--item-accent-alt", product.secondaryColor);
      link.style.setProperty("--item-soft", product.softColor);
      link.querySelector("[data-menu-featured-name]").textContent = product.name;
      const menu = link.closest("[data-mobile-menu]");
      menu?.style.setProperty("--menu-featured-accent", product.accentColor);
      menu?.style.setProperty("--menu-featured-secondary", product.secondaryColor);
      menu?.style.setProperty("--menu-featured-soft", product.softColor);
    });
    document.querySelectorAll("[data-spotlight]").forEach((container) => {
      if (!product) {
        container.hidden = true;
        return;
      }
      container.hidden = false;
      container.dataset.spotlightProduct = product.id;
      const first = roleMedia(product, "spotlightMediaIndex");
      container.style.setProperty("--spotlight-accent", product.accentColor);
      container.style.setProperty("--spotlight-accent-alt", product.secondaryColor);
      container.style.setProperty("--spotlight-soft", product.softColor);
      container.style.setProperty(
        "--spotlight-position",
        product.spotlightObjectPosition || "50% 50%",
      );
      const section = container.closest(".spotlight");
      section?.style.setProperty("--section-tint", product.softColor);
      section?.style.setProperty("--spotlight-accent", product.accentColor);
      container.innerHTML = `<article class="spotlight-card" data-reveal>
        <span class="spotlight__watermark" aria-hidden="true">PRESSD</span>
        <div class="spotlight__media">
          ${mediaMarkup(first, {
            className: "spotlight__image",
            loading: "lazy",
            sizes: "(max-width: 800px) 95vw, 58vw",
          })}
          <span class="spotlight__number">${escapeHTML(product.catalogCode)}</span>
        </div>
        <div class="spotlight__copy"><div class="spotlight__copy-inner">
          <span class="eyebrow">World of the week</span>
          <h3>${escapeHTML(product.name)}</h3>
          <p class="spotlight__tagline">${escapeHTML(product.worldTagline)}</p>
          <p class="spotlight__description">${escapeHTML(product.description)}</p>
          <div class="spotlight__footer"><strong class="spotlight__price">${escapeHTML(
            formatMoney(product.priceCents),
          )}</strong><a class="button" href="${productHref(
            product,
          )}" data-magnetic>Enter this world <span aria-hidden="true">↗</span></a></div>
        </div></div>
      </article>`;
    });
    setupRevealElements();
    setupMagneticElements();
  }

  function findProductFromLocation() {
    const params = new URLSearchParams(window.location.search);
    const reference =
      params.get("id") ||
      params.get("slug") ||
      document.body.dataset.productHandle;
    if (reference) return getProduct(reference);
    return products.find((product) => product.isRevealed) || products[0] || null;
  }

  function galleryStageMarkup(product, index) {
    const media = product.gallery[index] || product.gallery[0];
    if (!media || !media.src || media.revealPlaceholder) {
      return `<div class="gallery-placeholder" role="img" aria-label="${escapeHTML(
        media?.alt || "Product image unavailable",
      )}"><div class="gallery-placeholder__inner"><span>${escapeHTML(
        product.catalogCode,
      )}</span><strong>Product image unavailable</strong><p>Please use the product details while the image is unavailable.</p></div></div>`;
    }

    return `${mediaMarkup(media, {
      className: "pdp-gallery__main-image",
      loading: index === 0 ? "eager" : "lazy",
      sizes: "(max-width: 850px) 100vw, 58vw",
    })}<button class="zoom-trigger" type="button" data-gallery-zoom="${index}" aria-label="Zoom ${escapeHTML(
      media.alt,
    )}">Open detail <span aria-hidden="true">↗</span></button>`;
  }

  function galleryThumbMarkup(media, index, activeIndex) {
    const visual = media?.src && !media.revealPlaceholder
      ? mediaMarkup(media, {
          className: "pdp-gallery__thumb-image",
          loading: "lazy",
          decorative: true,
          sizes: "96px",
        })
      : '<span class="pdp-gallery__thumb-placeholder" aria-hidden="true">Unavailable</span>';
    return `<button type="button" class="thumbnail-button" data-gallery-thumb="${index}" aria-label="${escapeHTML(
      media?.alt || "View image placeholder",
    )}" aria-pressed="${String(index === activeIndex)}">${visual}</button>`;
  }

  function instructionMediaMarkup(product) {
    const media = product.instructionImage;
    if (!media) return "";
    return `<figure class="care-media">
      <img src="${escapeHTML(media.src)}" alt="${escapeHTML(
        media.alt,
      )}" width="${Number(media.width)}" height="${Number(
        media.height,
      )}" loading="lazy" decoding="async" sizes="(max-width: 850px) 90vw, 420px" data-media-kind="instructions" data-product-photo="true">
      <figcaption>Directions photographed from this set’s packaging.</figcaption>
    </figure>`;
  }

  function pdpKitMarkup() {
    return kit
      .map(
        (item) =>
          `<li><span aria-hidden="true">✦</span><div><strong>${escapeHTML(
            item.name,
          )}</strong><small>${escapeHTML(item.detail)}</small></div></li>`,
      )
      .join("");
  }

  function renderPdp(product = findProductFromLocation()) {
    const container = document.querySelector("[data-pdp]");
    if (!container) return;
    if (!product) {
      document.title = `World not found — ${settings.brandName}`;
      container.setAttribute("aria-busy", "false");
      container.innerHTML = `<div class="pdp-not-found"><span class="eyebrow">Signal lost</span><h1>This world isn’t in the archive.</h1><p>The link may be old or the set may no longer be available.</p><a class="button" href="${window.PRESSD_SHOPIFY_CONTEXT?.routes?.root || "/"}#collection">Return to the collection <span aria-hidden="true">↗</span></a></div>`;
      document.querySelector(".related")?.setAttribute("hidden", "");
      return;
    }
    document.querySelector(".related")?.removeAttribute("hidden");
    state.currentProduct = product;
    state.galleryIndex = 0;
    setProductTheme(product);
    document.title = `${product.name} — ${settings.brandName}`;
    const availabilityAction = !product.isRevealed
      ? `<a class="button" href="${productHref(product)}">View set <span aria-hidden="true">↗</span></a>`
      : product.canAddToCart
      ? `<button class="button" type="button" data-add-to-cart="${escapeHTML(
          product.id,
        )}" data-magnetic>Add to bag <span>${escapeHTML(
          formatMoney(product.priceCents),
        )}</span></button>`
      : '<button class="button" type="button" disabled>Sold out</button>';
    const complianceItems = [
      ["Prop 65 warning", product.compliance?.prop65],
      ["Ingredients / may contain", product.compliance?.ingredients],
    ]
      .filter(([, value]) => String(value || "").trim())
      .map(([label, value]) => `<div><strong>${escapeHTML(label)}</strong><p>${escapeHTML(value)}</p></div>`)
      .join("");

    container.style.setProperty("--pdp-accent", product.accentColor);
    container.style.setProperty("--pdp-accent-alt", product.secondaryColor);
    container.style.setProperty("--pdp-soft", product.softColor);
    container.innerHTML = `<div class="pdp__layout">
      <div class="pdp-gallery-wrap">
        <div class="pdp-gallery" data-pdp-gallery>
          <div class="pdp-thumbnails" aria-label="Product gallery">${product.gallery
            .map((media, index) => galleryThumbMarkup(media, index, 0))
            .join("")}</div>
          <div class="pdp-main-media" data-gallery-stage>${galleryStageMarkup(
            product,
            0,
          )}</div>
        </div>
        ${
          product.isRevealed
            ? ""
            : '<p class="pdp-gallery__truth">Product image unavailable</p>'
        }
      </div>
      <section class="pdp-buybox" aria-labelledby="pdp-title">
        <span class="eyebrow">${escapeHTML(product.catalogCode)} · ${escapeHTML(
          product.badge,
        )}</span>
        <h1 class="pdp-title" id="pdp-title">${escapeHTML(product.name)}</h1>
        <p class="pdp-tagline">${escapeHTML(product.worldTagline)}</p>
        <p class="pdp-price">${escapeHTML(formatMoney(product.priceCents))}</p>
        <p class="pdp-description">${escapeHTML(product.description)}</p>
        <ul class="composition-list" aria-label="Composition notes">${product.composition
          .map((note) => `<li>${escapeHTML(note)}</li>`)
          .join("")}</ul>
        <dl class="product-specs"><div><dt>Shape</dt><dd>${escapeHTML(
          product.shape,
        )}</dd></div><div><dt>Length</dt><dd>${escapeHTML(
          product.length,
        )}</dd></div></dl>
        <div class="sizing-note"><p>${escapeHTML(
          settings.sizingNote,
        )}</p><button type="button" data-open-size-guide>Open the size guide</button></div>
        <div class="pdp-actions">${availabilityAction}<p class="checkout-note">${escapeHTML(
          settings.wearNote,
        )}</p></div>
        <div class="mini-kit"><h2>Everything in the box</h2><ul>${pdpKitMarkup()}</ul></div>
        <div class="pdp-accordions" data-accordion data-single-open>
          <details><summary>Application ritual</summary><ol>${ritualSteps
            .map(
              (step) =>
                `<li><strong>${escapeHTML(step.title)}</strong><span>${escapeHTML(
                  step.copy,
                )}</span></li>`,
            )
            .join("")}</ol></details>
          <details><summary>Removal + reuse</summary><p>Follow the removal directions supplied with your adhesive, loosen patiently and never force. ${escapeHTML(
            settings.reuseNote,
          )}</p></details>
          <details><summary>Care notes</summary><p>Keep the set dry between wears, clear away residual adhesive gently and return each nail to its storage box.</p>${instructionMediaMarkup(
            product,
          )}</details>
        </div>
        ${complianceItems ? `<aside class="compliance-block" aria-label="Product compliance information"><strong>Listing information</strong>${complianceItems}</aside>` : ""}
      </section>
    </div>`;
    container.setAttribute("aria-busy", "false");
    renderStickyAtc(product);
    renderRelated(product);
    initAccordions(container);
    setupMagneticElements();
  }

  function updatePdpGallery(index) {
    const product = state.currentProduct;
    if (!product || !product.gallery[index]) return;
    state.galleryIndex = index;
    document.querySelectorAll("[data-gallery-stage]").forEach((stage) => {
      stage.innerHTML = galleryStageMarkup(product, index);
    });
    document.querySelectorAll("[data-gallery-thumb]").forEach((button) => {
      button.setAttribute(
        "aria-pressed",
        String(Number(button.dataset.galleryThumb) === index),
      );
    });
  }

  function ensureZoomDialog() {
    let dialog = document.querySelector(
      "[data-image-zoom-dialog], [data-zoom-dialog]",
    );
    if (dialog) {
      dialog.dataset.imageZoomDialog = "";
      return dialog;
    }
    dialog = document.createElement("dialog");
    dialog.className = "zoom-dialog";
    dialog.dataset.imageZoomDialog = "";
    dialog.setAttribute("aria-label", "Image detail");
    document.body.append(dialog);
    bindDialogLifecycle(dialog);
    return dialog;
  }

  function openGalleryZoom(index) {
    const product = state.currentProduct;
    const media = product?.gallery[index];
    if (!media?.src || media.revealPlaceholder) return;
    const dialog = ensureZoomDialog();
    dialog.removeAttribute("aria-labelledby");
    dialog.setAttribute("aria-label", "Expanded product image");
    dialog.innerHTML = `<button class="dialog-close" type="button" data-dialog-close aria-label="Close image detail">×</button><figure>${mediaMarkup(
      media,
      {
        className: "zoom-dialog__image",
        loading: "eager",
        sizes: "96vw",
      },
    )}<figcaption>${escapeHTML(media.alt)}</figcaption></figure>`;
    openDialog(dialog);
  }

  function sharedTagScore(first, second) {
    return ["mood", "color", "finish"].reduce((score, key) => {
      const shared = (first.tags[key] || []).filter((value) =>
        (second.tags[key] || []).includes(value),
      ).length;
      return score + shared;
    }, 0);
  }

  function getRelated(product, limit = 3, source = products) {
    return source
      .filter((candidate) => candidate.id !== product.id)
      .map((candidate, index) => ({
        product: candidate,
        score: sharedTagScore(product, candidate),
        index,
      }))
      .sort(
        (left, right) =>
          right.score - left.score ||
          Number(right.product.isRevealed) - Number(left.product.isRevealed) ||
          left.index - right.index,
      )
      .slice(0, limit)
      .map((entry) => entry.product);
  }

  function renderRelated(product) {
    document.querySelectorAll("[data-related-products]").forEach((container) => {
      const related = getRelated(product);
      container.innerHTML = related.map(productCardMarkup).join("");
    });
    setupRevealElements();
    setupMagneticElements();
  }

  function renderStickyAtc(product) {
    document.querySelectorAll("[data-sticky-atc]").forEach((container) => {
      container.classList.remove("is-visible");
      if (!product.isRevealed || !product.canAddToCart) {
        container.hidden = true;
        return;
      }
      container.hidden = false;
      container.style.setProperty("--sticky-accent", product.accentColor);
      container.innerHTML = `<div class="sticky-atc__meta"><strong>${escapeHTML(
        product.name,
      )}</strong><span>${escapeHTML(
        formatMoney(product.priceCents),
      )}</span></div><button class="button" type="button" data-add-to-cart="${escapeHTML(
        product.id,
      )}" ${product.canAddToCart ? "" : "disabled"}>${
        product.canAddToCart ? "Add to bag" : "Sold out"
      }</button>`;
    });
    initStickyAtcVisibility();
  }

  function initStickyAtcVisibility() {
    state.stickyAtcObserver?.disconnect();
    state.stickyAtcObserver = null;
    if (state.stickyAtcScrollHandler) {
      window.removeEventListener("scroll", state.stickyAtcScrollHandler);
      state.stickyAtcScrollHandler = null;
    }
    const sticky = document.querySelector("[data-sticky-atc]");
    const mainAction = document.querySelector(".pdp-actions [data-add-to-cart]");
    if (!sticky || sticky.hidden || !mainAction) return;

    const setVisible = (visible) => {
      sticky.classList.toggle("is-visible", Boolean(visible));
      sticky.setAttribute("aria-hidden", String(!visible));
    };

    setVisible(false);
    let scheduled = false;
    const update = () => {
      scheduled = false;
      const headerHeight =
        document.querySelector("[data-site-header]")?.getBoundingClientRect().height || 0;
      setVisible(mainAction.getBoundingClientRect().bottom <= headerHeight);
    };
    state.stickyAtcScrollHandler = () => {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(update);
    };
    window.addEventListener("scroll", state.stickyAtcScrollHandler, { passive: true });
    update();
  }

  function measurementVariants(product) {
    return (product?.variants || []).filter(
      (variant) => variant.measurementKey && SIZE_MEASUREMENTS[variant.measurementKey],
    );
  }

  function measurementTableMarkup(product, preferredVariant) {
    const variants = measurementVariants(product);
    if (!variants.length) return "";
    const activeVariant = variants.find(
      (variant) => String(variant.id) === String(preferredVariant?.id || state.sizeGuideVariantId),
    ) || variants.find((variant) => variant.available) || variants[0];
    state.sizeGuideVariantId = String(activeVariant.id);
    const rows = SIZE_MEASUREMENTS[activeVariant.measurementKey]
      .map(([finger, tip, length, width]) => `<tr>
        <th scope="row">${escapeHTML(finger)}</th>
        <td>${escapeHTML(tip)}</td>
        <td>${escapeHTML(length)} mm</td>
        <td>${escapeHTML(width)} mm</td>
      </tr>`)
      .join("");
    const tabs = variants
      .map((variant) => {
        const selected = String(variant.id) === String(activeVariant.id);
        const soldOut = !variant.available;
        return `<button type="button" role="tab" aria-selected="${String(selected)}" tabindex="${selected ? "0" : "-1"}" data-size-chart-variant="${escapeHTML(variant.id)}"${soldOut ? ' data-sold-out="true"' : ""}>${escapeHTML(variant.measurementLabel || variant.title)}${soldOut ? " · Sold out" : ""}</button>`;
      })
      .join("");
    const activeLabel = activeVariant.measurementLabel || activeVariant.title;
    return `<section class="size-dialog__measurements" data-size-dialog-measurements>
      <div class="size-dialog__measurement-heading">
        <div><span class="eyebrow">Exact numbers for ${escapeHTML(product.name)}</span><h3>${escapeHTML(activeLabel)}</h3></div>
        <small>Measurements in mm</small>
      </div>
      <div class="size-dialog__size-tabs" role="tablist" aria-label="Choose a size chart">${tabs}</div>
      <p class="size-dialog__measurement-note">Use width as the main fit check. Tip number and length are included for reference.</p>
      <div class="size-dialog__measurement-table">
        <table>
          <caption class="visually-hidden">Tip, length and width measurements for ${escapeHTML(product.name)}, ${escapeHTML(activeLabel)}</caption>
          <thead><tr><th scope="col">Finger</th><th scope="col">Tip</th><th scope="col">Length</th><th scope="col">Width</th></tr></thead>
          <tbody>${rows}</tbody>
        </table>
      </div>
      <button class="button size-dialog__choose" type="button" data-size-chart-choose="${escapeHTML(activeVariant.id)}"${activeVariant.available ? "" : " disabled"}>${activeVariant.available ? `Choose ${escapeHTML(activeLabel)}` : `${escapeHTML(activeLabel)} is sold out`}</button>
    </section>`;
  }

  function ensureSizeDialog(product = null, preferredVariant = null) {
    let dialog = document.querySelector("[data-global-size-dialog]");
    const isNew = !dialog;
    if (!dialog) {
      dialog = document.createElement("dialog");
      dialog.className = "size-dialog size-dialog--global";
      dialog.dataset.sizeDialog = "";
      dialog.dataset.globalSizeDialog = "";
      dialog.setAttribute("aria-labelledby", "global-size-guide-title");
      document.body.append(dialog);
    }
    const guideImages = settings.sizeGuideImages || {};
    const guideSteps = [
      [guideImages.step1, "Step 1: Place a small piece of clear tape across the widest part of your natural nail."],
      [guideImages.step2, "Step 2: Mark the left and right edges of your nail on the tape."],
      [guideImages.step3, "Step 3: Remove the tape, place it flat against a ruler and measure between the marks in millimetres."],
    ].filter(([src]) => src);
    const guideMarkup = guideSteps.length
      ? `<section class="size-dialog__image-guide" aria-labelledby="global-size-measure-title">
          <div class="size-dialog__guide-heading"><h3 id="global-size-measure-title">Measure in three quick steps</h3><span>Swipe to see each step</span></div>
          <div class="size-dialog__guide-track" role="list">${guideSteps.map(([src, alt]) => `<figure role="listitem"><img src="${escapeHTML(src)}" alt="${escapeHTML(alt)}" width="1122" height="1402" loading="lazy"></figure>`).join("")}</div>
        </section>`
      : "";
    const fitExampleMarkup = guideImages.fitExample
      ? `<figure class="size-dialog__fit-example"><img src="${escapeHTML(guideImages.fitExample)}" alt="Example showing nail width measurements and how to compare them with a size chart." width="1212" height="1297" loading="lazy"><figcaption>Example only—use the exact chart for your selected set.</figcaption></figure>`
      : "";
    dialog.innerHTML = `<button class="dialog-close" type="button" data-dialog-close aria-label="Close size guide">×</button>
      <span class="eyebrow">Fit, pressed flat</span>
      <h2 id="global-size-guide-title">Find your closest fit.</h2>
      <p>Measure the widest part of each natural nail in millimetres.</p>
      ${guideMarkup}
      <p class="size-dialog__note">Between sizes? Choose the larger tip and file the sides carefully.</p>
      <div class="size-dialog__fit-layout">${fitExampleMarkup}${measurementTableMarkup(product, preferredVariant)}</div>
      <button class="button button--outline size-dialog__print" type="button" data-print-size-guide>Print this size worksheet</button>`;
    if (isNew) bindDialogLifecycle(dialog);
    return dialog;
  }

  function cartEndpoint(route, fallback) {
    const value = String(route || fallback || "");
    return value.endsWith(".js") ? value : `${value}.js`;
  }

  async function cartRequest(route, options = {}) {
    const response = await fetch(route, {
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
        ...(options.body ? { "Content-Type": "application/json" } : {}),
        ...(options.headers || {}),
      },
      ...options,
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.description || payload.message || "The bag could not be updated.");
    }
    return payload;
  }

  function cartCount() {
    return Number(state.cart?.item_count) || 0;
  }

  function cartSubtotal() {
    return Number(state.cart?.total_price) || 0;
  }

  function shippingProgressMarkup() {
    return '<div class="shipping-progress"><p><strong>Shipping\'s on us.</strong> Free U.S. shipping with no minimum, for a limited time.</p></div>';
  }

  async function refreshCart({ render = true } = {}) {
    try {
      state.cart = await cartRequest(
        cartEndpoint(window.PRESSD_SHOPIFY_CONTEXT?.routes?.cart, "/cart"),
        { method: "GET", cache: "no-store" },
      );
      state.cartError = "";
    } catch (error) {
      state.cartError = error instanceof Error ? error.message : "The bag could not be loaded.";
    }
    if (render) renderCart();
    return state.cart;
  }

  async function addToCart(variantId, quantity = 1) {
    const id = Number(variantId);
    if (!Number.isFinite(id) || id <= 0 || state.cartPending) return false;
    state.cartPending = true;
    state.cartError = "";
    try {
      await cartRequest(
        cartEndpoint(window.PRESSD_SHOPIFY_CONTEXT?.routes?.cartAdd, "/cart/add"),
        {
          method: "POST",
          body: JSON.stringify({ items: [{ id, quantity: Math.max(1, Number(quantity) || 1) }] }),
        },
      );
      await refreshCart({ render: false });
      renderCart();
      return true;
    } catch (error) {
      state.cartError = error instanceof Error ? error.message : "This set could not be added.";
      renderCart();
      return false;
    } finally {
      state.cartPending = false;
    }
  }

  function addSourceImage(button) {
    const root = button.closest(
      ".product-card, .pdp-page, [data-pdp], [data-product-section], .quick-add-sheet",
    );
    const candidates = root
      ? [
          ...root.querySelectorAll(
            ".product-card__image, .pdp-gallery__main-image, .pdp-gallery__thumb-image, .quick-add-sheet__image",
          ),
        ]
      : [];
    return (
      candidates.find((image) => {
        const style = window.getComputedStyle(image);
        const rect = image.getBoundingClientRect();
        return style.opacity !== "0" && rect.width > 0 && rect.height > 0;
      }) || null
    );
  }

  function flyThumbnailToBag(button) {
    const source = addSourceImage(button);
    const bag = document.querySelector("[data-cart-open]");
    if (!source || !bag) return Promise.resolve();
    const sourceRect = source.getBoundingClientRect();
    const bagRect = bag.getBoundingClientRect();
    const size = Math.min(64, Math.max(46, sourceRect.width * 0.22));
    const image = document.createElement("img");
    image.className = "cart-fly-thumb";
    image.src = source.currentSrc || source.src;
    image.alt = "";
    image.width = size;
    image.height = size;
    image.setAttribute("aria-hidden", "true");
    image.style.left = `${sourceRect.left + sourceRect.width / 2 - size / 2}px`;
    image.style.top = `${sourceRect.top + sourceRect.height / 2 - size / 2}px`;
    document.body.append(image);
    const deltaX =
      bagRect.left + bagRect.width / 2 - (sourceRect.left + sourceRect.width / 2);
    const deltaY =
      bagRect.top + bagRect.height / 2 - (sourceRect.top + sourceRect.height / 2);
    const animation = image.animate(
      [
        { transform: "translate3d(0,0,0) scale(1) rotate(0deg)", opacity: 0.96 },
        {
          transform: `translate3d(${deltaX}px,${deltaY}px,0) scale(.18) rotate(8deg)`,
          opacity: 0.2,
        },
      ],
      { duration: 500, easing: "cubic-bezier(.16,1,.3,1)", fill: "forwards" },
    );
    return animation.finished
      .catch(() => {})
      .finally(() => image.remove());
  }

  async function playAddToBagDelight(button) {
    if (reducedMotionQuery.matches) return;
    const original = button.innerHTML;
    button.dataset.addPending = "true";
    button.setAttribute("aria-busy", "true");
    button.classList.add("is-added");
    button.innerHTML = 'Added <span aria-hidden="true">✦</span>';
    document.querySelectorAll("[data-cart-open], [data-cart-count]").forEach((element) => {
      element.classList.remove("is-bouncing");
      void element.offsetWidth;
      element.classList.add("is-bouncing");
    });
    await Promise.allSettled([
      flyThumbnailToBag(button),
      new Promise((resolve) => window.setTimeout(resolve, 520)),
    ]);
    button.innerHTML = original;
    button.classList.remove("is-added");
    button.removeAttribute("aria-busy");
    delete button.dataset.addPending;
  }

  async function setCartQuantity(lineKey, quantity) {
    if (!lineKey || state.cartPending) return false;
    state.cartPending = true;
    state.cartError = "";
    try {
      state.cart = await cartRequest(
        cartEndpoint(window.PRESSD_SHOPIFY_CONTEXT?.routes?.cartChange, "/cart/change"),
        {
          method: "POST",
          body: JSON.stringify({ id: lineKey, quantity: Math.max(0, Number(quantity) || 0) }),
        },
      );
      renderCart();
      return true;
    } catch (error) {
      state.cartError = error instanceof Error ? error.message : "The bag could not be updated.";
      renderCart();
      return false;
    } finally {
      state.cartPending = false;
    }
  }

  function restoreCartFocus(selector) {
    window.requestAnimationFrame(() => {
      document
        .querySelector("[data-cart-dialog]")
        ?.querySelector(selector)
        ?.focus();
    });
  }

  function ensureCartDialog() {
    let dialog = document.querySelector("[data-cart-dialog]");
    if (dialog) return dialog;
    dialog = document.createElement("dialog");
    dialog.className = "cart-drawer";
    dialog.dataset.cartDialog = "";
    dialog.setAttribute("aria-labelledby", "cart-title");
    document.body.append(dialog);
    bindDialogLifecycle(dialog);
    return dialog;
  }

  function renderCart() {
    const count = cartCount();
    document.querySelectorAll("[data-cart-count]").forEach((element) => {
      element.textContent = String(count);
      element.hidden = false;
    });

    const dialog = ensureCartDialog();
    dialog.setAttribute("aria-labelledby", "cart-title");
    const items = Array.isArray(state.cart?.items) ? state.cart.items : [];
    const lines = items
      .map((item) => {
        const lineKey = String(item.key || item.id || "");
        const productUrl = item.url || `${window.PRESSD_SHOPIFY_CONTEXT?.routes?.root || "/"}products/${encodeURIComponent(item.handle || "")}`;
        const image = item.featured_image?.url || item.image || "";
        const imageAlt = item.featured_image?.alt || item.product_title || "Pressd Nails set";
        const variantTitle = item.variant_title && item.variant_title !== "Default Title" ? item.variant_title : "";
        const media = image
          ? `<img class="cart-line__media" src="${escapeHTML(image)}" alt="${escapeHTML(imageAlt)}" width="96" height="120" loading="lazy">`
          : '<span class="cart-line__media media-placeholder" aria-hidden="true"></span>';
        return `<li class="cart-line" data-cart-line="${escapeHTML(lineKey)}">
          <a class="cart-line__image" href="${escapeHTML(productUrl)}">${media}</a>
          <div class="cart-line__copy"><span class="eyebrow">${escapeHTML(
            item.sku || "Pressd world",
          )}</span><h3><a href="${escapeHTML(productUrl)}">${escapeHTML(
            item.product_title || item.title || "Pressd Nails set",
          )}</a></h3>${variantTitle ? `<p class="cart-line__variant">${escapeHTML(variantTitle)}</p>` : ""}<p class="cart-line__price">${item.quantity} × ${escapeHTML(
            formatMoney(Number(item.final_price) || 0),
          )}</p>
            <div class="quantity-control" aria-label="Quantity for ${escapeHTML(
              item.product_title || item.title || "Pressd Nails set",
            )}"><button type="button" data-cart-decrease="${escapeHTML(
              lineKey,
            )}" aria-label="Decrease quantity">−</button><output>${item.quantity}</output><button type="button" data-cart-increase="${escapeHTML(
              lineKey,
            )}" aria-label="Increase quantity">+</button></div>
            <button class="cart-remove" type="button" data-cart-remove="${escapeHTML(
              lineKey,
            )}">Remove</button>
          </div>
          <strong class="cart-line__total"><span class="visually-hidden">Line total </span>${escapeHTML(
            formatMoney(Number(item.final_line_price) || 0),
          )}</strong>
        </li>`;
      })
      .join("");

    dialog.innerHTML = `<div class="cart-drawer__inner">
      <header class="cart-drawer__header"><div><span class="eyebrow">Your edit</span><h2 id="cart-title">Bag <small>${count}</small></h2></div><button class="dialog-close" type="button" data-cart-close aria-label="Close bag">×</button></header>
      <div class="cart-drawer__body">${state.cartError ? `<p class="cart-drawer__error" role="alert">${escapeHTML(state.cartError)}</p>` : ""}${
        lines
          ? `<ul class="cart-items" data-cart-items>${lines}</ul>`
          : '<div class="cart-empty"><span class="cart-empty__illustration" aria-hidden="true"><i></i><b>PN</b></span><h3>Your next world starts here.</h3><p>The bag is ready when you are.</p><button class="button button--outline" type="button" data-cart-close>Keep looking</button></div>'
      }</div>
      <footer class="cart-drawer__footer">${shippingProgressMarkup()}<div class="cart-subtotal"><span>Subtotal</span><strong>${escapeHTML(
        formatMoney(cartSubtotal()),
      )}</strong></div><p class="cart-disclaimer">Taxes and non-U.S. shipping are calculated at checkout.</p>${
        count > 0
          ? `<a class="button" href="${escapeHTML(window.PRESSD_SHOPIFY_CONTEXT?.routes?.checkout || "/checkout")}">Checkout</a>`
          : '<button class="button" type="button" disabled>Checkout</button>'
      }</footer>
    </div>`;
  }

  function bindDialogLifecycle(dialog) {
    if (dialog.dataset.lifecycleBound === "true") return;
    dialog.dataset.lifecycleBound = "true";
    dialog.addEventListener("click", (event) => {
      if (event.target === dialog) closeDialog(dialog);
    });
    dialog.addEventListener("close", () => {
      if (!document.querySelector("dialog[open]")) {
        document.body.classList.remove("has-dialog");
      }
    });
  }

  function openDialog(dialog) {
    if (!dialog) return;
    document.body.classList.add("has-dialog");
    if (typeof dialog.showModal === "function") {
      if (!dialog.open) dialog.showModal();
    } else {
      dialog.setAttribute("open", "");
      dialog.setAttribute("aria-modal", "true");
    }
    window.requestAnimationFrame(() => {
      dialog.querySelector("button, a, input, select")?.focus();
    });
  }

  function closeDialog(dialog) {
    if (!dialog) return;
    if (typeof dialog.close === "function" && dialog.open) dialog.close();
    else dialog.removeAttribute("open");
    if (!document.querySelector("dialog[open]")) {
      document.body.classList.remove("has-dialog");
    }
  }

  function initNewsletterForms() {
    document.querySelectorAll("[data-newsletter-form]").forEach((form) => {
      if (form.dataset.bound === "true") return;
      form.dataset.bound = "true";
      const input = form.querySelector('input[type="email"]');
      let status = form.querySelector("[data-newsletter-status]");
      if (!status) {
        status = document.createElement("p");
        status.dataset.newsletterStatus = "";
        status.setAttribute("aria-live", "polite");
        form.append(status);
      }
      input?.addEventListener("input", () => {
        input.removeAttribute("aria-invalid");
        status.textContent = "";
      });
      form.addEventListener("submit", (event) => {
        event.preventDefault();
        const value = input?.value.trim() || "";
        const looksLikeEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
        if (!looksLikeEmail) {
          input?.setAttribute("aria-invalid", "true");
          status.textContent = "Press pause — enter a complete email address.";
          input?.focus();
          return;
        }
        input?.removeAttribute("aria-invalid");
        status.textContent = settings.newsletterSuccessMessage;
        form.classList.add("is-success");
      });
    });
  }

  function initNativeDynamicCheckout() {
    if (!document.querySelector("[data-native-product-form] [data-dynamic-checkout]")) return;
    window.Shopify?.PaymentButton?.init?.();
  }

  function initMobileMenu() {
    const menu = document.querySelector("[data-mobile-menu]");
    const toggles = [...document.querySelectorAll("[data-menu-toggle]")];
    const desktopQuery = window.matchMedia("(min-width: 51.25rem)");
    if (!menu || !toggles.length) return;
    const header = document.querySelector("[data-site-header]");
    const cartButton = header?.querySelector("[data-cart-open]");
    const menuPanel = menu.querySelector("[data-menu-panel]");
    let closeTimer = 0;
    let returnFocusAfterClose = false;
    if (!menu.id) menu.id = "mobile-navigation";
    toggles.forEach((button) => {
      button.setAttribute("aria-controls", menu.id);
      button.setAttribute("aria-expanded", "false");
    });

    const updateMenuTop = () => {
      if (!menu.classList.contains("is-open")) return;
      const headerBottom = header?.getBoundingClientRect().bottom;
      if (Number.isFinite(headerBottom)) {
        menu.style.setProperty("--mobile-menu-top", `${Math.max(0, Math.round(headerBottom))}px`);
      }
    };

    const pinHeaderForMenu = () => {
      const headerTop = header?.getBoundingClientRect().top;
      if (Number.isFinite(headerTop)) {
        header.style.setProperty("--mobile-menu-header-top", `${Math.max(0, Math.round(headerTop))}px`);
      }
    };

    const setSharedState = (open) => {
      document.body.classList.toggle("menu-open", open);
      document
        .querySelectorAll("main, .site-footer")
        .forEach((element) => element.toggleAttribute("inert", open));
      toggles.forEach((button) => {
        button.setAttribute("aria-expanded", String(open));
        button.querySelector("[data-menu-label]").textContent = open ? "Close menu" : "Menu";
      });
    };

    const finishClose = (returnFocus = returnFocusAfterClose) => {
      window.clearTimeout(closeTimer);
      closeTimer = 0;
      returnFocusAfterClose = false;
      menu.classList.remove("is-open", "is-closing");
      menu.hidden = true;
      menu.style.removeProperty("--mobile-menu-top");
      setSharedState(false);
      header?.style.removeProperty("--mobile-menu-header-top");
      if (returnFocus) toggles[0]?.focus();
    };

    const setOpen = (open, returnFocus = false, immediate = false) => {
      open = Boolean(open);
      window.clearTimeout(closeTimer);
      closeTimer = 0;
      if (open) {
        returnFocusAfterClose = false;
        menu.hidden = false;
        menu.classList.remove("is-closing");
        pinHeaderForMenu();
        setSharedState(true);
        updateMenuTop();
        window.requestAnimationFrame(() => {
          menu.classList.add("is-open");
          updateMenuTop();
          toggles[0]?.focus({ preventScroll: true });
        });
        return;
      }

      returnFocusAfterClose = Boolean(returnFocus);
      menu.classList.remove("is-open");
      if (immediate || reducedMotionQuery.matches || menu.hidden) {
        finishClose();
        return;
      }
      menu.classList.add("is-closing");
      closeTimer = window.setTimeout(() => finishClose(), 220);
    };

    toggles.forEach((button) => {
      button.addEventListener("click", () => {
        const shouldOpen = menu.hidden || menu.classList.contains("is-closing");
        setOpen(shouldOpen);
      });
    });
    menu.addEventListener("click", (event) => {
      if (event.target.closest("[data-menu-backdrop]")) {
        setOpen(false, true);
      } else if (event.target.closest("a")) {
        // Close atomically before same-page hash navigation. Waiting for the
        // exit animation here can leave the sheet and its toggle out of sync
        // if the browser begins scrolling or restores a page from bfcache.
        setOpen(false, false, true);
      }
    });
    menuPanel?.addEventListener("animationend", (event) => {
      if (
        event.target === menuPanel &&
        event.animationName === "pressd-menu-panel-out" &&
        menu.classList.contains("is-closing")
      ) {
        finishClose();
      }
    });
    document.addEventListener(
      "click",
      (event) => {
        if (menu.classList.contains("is-open") && event.target.closest("[data-cart-open]")) {
          setOpen(false);
        }
      },
      true,
    );
    document.addEventListener("keydown", (event) => {
      if (!menu.classList.contains("is-open")) return;
      if (event.key === "Escape") {
        setOpen(false, true);
        return;
      }
      if (event.key === "Tab") {
        const focusable = [
          toggles[0],
          cartButton,
          ...menu.querySelectorAll('a[href], button:not([disabled]):not([tabindex="-1"])'),
        ].filter(Boolean);
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable.at(-1);
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    });
    desktopQuery.addEventListener?.("change", (event) => {
      if (event.matches) setOpen(false, false, true);
    });
    window.addEventListener("hashchange", () => {
      if (!menu.hidden) setOpen(false, false, true);
    });
    window.addEventListener("pagehide", () => setOpen(false, false, true));
    window.addEventListener("pageshow", () => setOpen(false, false, true));
    window.addEventListener("resize", updateMenuTop, { passive: true });
    window.addEventListener("scroll", updateMenuTop, { passive: true });
    setOpen(false, false, true);
  }

  function initAccordions(root = document) {
    root.querySelectorAll("[data-accordion]").forEach((accordion) => {
      if (accordion.dataset.bound === "true") return;
      accordion.dataset.bound = "true";
      accordion.querySelectorAll("details").forEach((details) => {
        details.addEventListener("toggle", () => {
          if (!details.open || !accordion.hasAttribute("data-single-open")) return;
          accordion.querySelectorAll("details[open]").forEach((other) => {
            if (other !== details) other.open = false;
          });
        });
      });
    });
  }

  function setupRevealElements(root = document) {
    const elements = [...root.querySelectorAll("[data-reveal], .reveal")];
    if (reducedMotionQuery.matches || !("IntersectionObserver" in window)) {
      elements.forEach((element) => element.classList.add("is-visible"));
      return;
    }
    document.documentElement.classList.add("motion-ready");
    if (!state.revealObserver) {
      state.revealObserver = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          });
        },
        { rootMargin: "0px 0px -10% 0px", threshold: 0 },
      );
    }
    elements.forEach((element) => {
      if (observedReveals.has(element)) return;
      observedReveals.add(element);
      const rect = element.getBoundingClientRect();
      if (rect.top <= window.innerHeight * 0.92 && rect.bottom >= 0) {
        element.classList.add("is-visible");
        return;
      }
      state.revealObserver.observe(element);
    });
  }

  function setupMagneticElements(root = document) {
    if (reducedMotionQuery.matches || !finePointerQuery.matches) return;
    root.querySelectorAll("[data-magnetic]").forEach((element) => {
      if (magnetizedElements.has(element)) return;
      magnetizedElements.add(element);
      element.addEventListener("pointermove", (event) => {
        const rect = element.getBoundingClientRect();
        const x = ((event.clientX - rect.left) / rect.width - 0.5) * 10;
        const y = ((event.clientY - rect.top) / rect.height - 0.5) * 8;
        element.style.setProperty("--magnetic-x", `${x.toFixed(2)}px`);
        element.style.setProperty("--magnetic-y", `${y.toFixed(2)}px`);
      });
      element.addEventListener("pointerleave", () => {
        element.style.setProperty("--magnetic-x", "0px");
        element.style.setProperty("--magnetic-y", "0px");
      });
    });
  }

  function initHeroParallax() {
    if (reducedMotionQuery.matches || !document.querySelector("[data-hero]")) return;
    let scheduled = false;
    const update = () => {
      scheduled = false;
      document.querySelectorAll("[data-hero]").forEach((hero) => {
        const rect = hero.getBoundingClientRect();
        const progress = Math.max(-1, Math.min(1, -rect.top / Math.max(rect.height, 1)));
        hero.style.setProperty("--parallax-y", `${(progress * 22).toFixed(2)}px`);
      });
    };
    window.addEventListener(
      "scroll",
      () => {
        if (scheduled) return;
        scheduled = true;
        window.requestAnimationFrame(update);
      },
      { passive: true },
    );
    update();
  }

  function initScrollProgress() {
    const indicators = [...document.querySelectorAll("[data-scroll-progress]")];
    if (!indicators.length) return;
    let scheduled = false;
    const update = () => {
      scheduled = false;
      const maxScroll = Math.max(
        1,
        document.documentElement.scrollHeight - window.innerHeight,
      );
      const progress = Math.max(0, Math.min(1, window.scrollY / maxScroll));
      indicators.forEach((indicator) => {
        indicator.style.transform = `scaleX(${progress.toFixed(4)})`;
      });
    };
    window.addEventListener(
      "scroll",
      () => {
        if (scheduled) return;
        scheduled = true;
        window.requestAnimationFrame(update);
      },
      { passive: true },
    );
    update();
  }

  function handleImageFailure(image) {
    if (!(image instanceof HTMLImageElement) || !image.dataset.mediaKind) return;
    const replacement = document.createElement("div");
    replacement.className = `media-fallback media-fallback--${image.dataset.mediaKind}`;
    replacement.setAttribute("role", "img");
    replacement.setAttribute("aria-label", image.alt || "Visual unavailable");
    replacement.innerHTML = `<span>${
      "Image unavailable"
    }</span>`;
    image.replaceWith(replacement);
  }

  async function handleDocumentClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest("[data-policy-placeholder]")) {
      event.preventDefault();
      return;
    }

    const heroControl = target.closest("[data-hero-control]");
    if (heroControl) {
      const control = heroControl.dataset.heroControl;
      moveHero(
        control === "next" ? 1 : -1,
        `[data-hero-control="${control}"]`,
      );
      return;
    }
    const heroDot = target.closest("[data-hero-index]");
    if (heroDot) {
      const nextIndex = Number(heroDot.dataset.heroIndex) || 0;
      switchHero(nextIndex, `[data-hero-index="${nextIndex}"]`);
      startHeroRotation();
      return;
    }
    const filterReset = target.closest("[data-filter-reset]");
    if (filterReset) {
      state.filters = { mood: "", color: "", finish: "" };
      renderFilters();
      renderCollection();
      return;
    }
    const quickAddOpen = target.closest("[data-quick-add-open]");
    if (quickAddOpen) {
      event.preventDefault();
      if (!quickAddOpen.disabled) openQuickAdd(quickAddOpen.dataset.quickAddOpen);
      return;
    }
    const galleryThumb = target.closest("[data-pdp] [data-gallery-thumb]");
    if (galleryThumb) {
      updatePdpGallery(Number(galleryThumb.dataset.galleryThumb));
      return;
    }
    const galleryZoom = target.closest("[data-pdp] [data-gallery-zoom]");
    if (galleryZoom) {
      openGalleryZoom(Number(galleryZoom.dataset.galleryZoom));
      return;
    }
    const sizeChartVariant = target.closest("[data-size-chart-variant]");
    if (sizeChartVariant) {
      event.preventDefault();
      const product = state.quickAddProduct;
      const variant = product?.variants?.find(
        (item) => String(item.id) === String(sizeChartVariant.dataset.sizeChartVariant),
      );
      if (!product || !variant) return;
      state.sizeGuideVariantId = String(variant.id);
      ensureSizeDialog(product, variant);
      window.requestAnimationFrame(() => {
        document.querySelector(`[data-size-chart-variant="${CSS.escape(String(variant.id))}"]`)?.focus();
      });
      return;
    }
    const sizeChartChoose = target.closest("[data-size-chart-choose]");
    if (sizeChartChoose) {
      event.preventDefault();
      const product = state.quickAddProduct;
      const variant = product?.variants?.find(
        (item) => String(item.id) === String(sizeChartChoose.dataset.sizeChartChoose),
      );
      if (!product || !variant?.available) return;
      closeDialog(sizeChartChoose.closest("dialog"));
      const quickAddDialog = renderQuickAddSheet(product);
      state.quickAddSelections = [...(variant.options || [])];
      quickAddDialog.querySelectorAll("[data-quick-add-option]").forEach((input) => {
        const optionIndex = Number(input.dataset.quickAddOption);
        input.checked = String(variant.options?.[optionIndex] || "") === input.value;
      });
      syncQuickAddSheet();
      openDialog(quickAddDialog);
      return;
    }
    if (target.closest("[data-open-size-guide]")) {
      event.preventDefault();
      const productSection = target.closest("[data-product-section]");
      const productSizeDialog = productSection?.querySelector("[data-size-dialog]");
      if (productSizeDialog) {
        openDialog(productSizeDialog);
        return;
      }
      const quickAddDialog = target.closest("[data-quick-add-dialog]");
      const guideProduct = quickAddDialog ? state.quickAddProduct : null;
      const selectedVariant = guideProduct ? selectedQuickAddVariant(guideProduct) : null;
      if (quickAddDialog) closeDialog(quickAddDialog);
      openDialog(ensureSizeDialog(guideProduct, selectedVariant));
      return;
    }
    if (target.closest("[data-print-size-guide]")) {
      window.print();
      return;
    }
    const addButton = target.closest(
      "[data-add-to-cart], [data-cart-add], [data-native-add-to-cart]",
    );
    if (addButton) {
      event.preventDefault();
      if (addButton.dataset.addPending === "true") return;
      const form = addButton.closest("form");
      const variantId =
        addButton.dataset.variantId ||
        addButton.dataset.addToCart ||
        form?.querySelector('input[name="id"]')?.value;
      const quantity = Number(form?.querySelector('[name="quantity"]')?.value) || 1;
      if (await addToCart(variantId, quantity)) {
        await playAddToBagDelight(addButton);
        openDialog(ensureCartDialog());
      } else {
        openDialog(ensureCartDialog());
      }
      return;
    }
    if (target.closest("[data-cart-open]")) {
      await refreshCart();
      openDialog(ensureCartDialog());
      return;
    }
    if (target.closest("[data-cart-close]")) {
      closeDialog(target.closest("dialog"));
      return;
    }
    const decrease = target.closest("[data-cart-decrease]");
    if (decrease) {
      const item = (state.cart.items || []).find(
        (entry) => entry.key === decrease.dataset.cartDecrease,
      );
      if (item) {
        const nextQuantity = item.quantity - 1;
        await setCartQuantity(item.key, nextQuantity);
        restoreCartFocus(
          nextQuantity > 0
            ? `[data-cart-decrease="${CSS.escape(item.key)}"]`
            : "[data-cart-close]",
        );
      }
      return;
    }
    const increase = target.closest("[data-cart-increase]");
    if (increase) {
      const item = (state.cart.items || []).find(
        (entry) => entry.key === increase.dataset.cartIncrease,
      );
      if (item) {
        await setCartQuantity(item.key, item.quantity + 1);
        restoreCartFocus(`[data-cart-increase="${CSS.escape(item.key)}"]`);
      }
      return;
    }
    const remove = target.closest("[data-cart-remove]");
    if (remove) {
      await setCartQuantity(remove.dataset.cartRemove, 0);
      restoreCartFocus("[data-cart-close]");
      return;
    }
    if (target.closest("[data-dialog-close]")) {
      closeDialog(target.closest("dialog"));
    }
  }

  async function handleDocumentSubmit(event) {
    const quickAddForm = event.target.closest?.("form[data-quick-add-form]");
    if (quickAddForm) {
      event.preventDefault();
      const button = event.submitter?.matches?.("[data-quick-add-submit]")
        ? event.submitter
        : quickAddForm.querySelector("[data-quick-add-submit]");
      const variantId = button?.dataset.variantId;
      if (!button || button.disabled || !variantId || button.dataset.addPending === "true") {
        syncQuickAddSheet();
        return;
      }
      const quickAddDialog = quickAddForm.closest("[data-quick-add-dialog]");
      if (await addToCart(variantId, 1)) {
        await playAddToBagDelight(button);
        closeDialog(quickAddDialog);
      }
      openDialog(ensureCartDialog());
      return;
    }
    const form = event.target.closest?.("form[data-native-product-form]");
    if (!form) return;
    event.preventDefault();
    const submitter = event.submitter instanceof Element ? event.submitter : null;
    const button = submitter?.matches("[data-sticky-submit]")
      ? submitter
      : form.querySelector(
          "[data-cart-add], [data-native-add-to-cart], [data-add-to-cart]",
        );
    if (!button || button.disabled || button.dataset.addPending === "true") return;
    const variantId =
      button.dataset.variantId ||
      button.dataset.addToCart ||
      form.querySelector('input[name="id"]')?.value;
    const quantity = Number(form.querySelector('[name="quantity"]')?.value) || 1;
    if (await addToCart(variantId, quantity)) {
      await playAddToBagDelight(button);
    }
    openDialog(ensureCartDialog());
  }

  function handleDocumentChange(event) {
    const quickAddOption = event.target.closest?.("[data-quick-add-option]");
    if (quickAddOption) {
      const optionIndex = Number(quickAddOption.dataset.quickAddOption);
      state.quickAddSelections[optionIndex] = quickAddOption.value;
      syncQuickAddSheet();
      return;
    }
    const select = event.target.closest?.("[data-filter-key]");
    if (!select) return;
    state.filters[select.dataset.filterKey] = select.value;
    renderFilters();
    renderCollection();
  }

  function validateCatalog(source = products) {
    const errors = [];
    const ids = new Set();
    const slugs = new Set();
    source.forEach((product, index) => {
      const prefix = `products[${index}]`;
      if (!product.id || ids.has(product.id)) errors.push(`${prefix}: invalid or duplicate id`);
      if (!product.slug || slugs.has(product.slug))
        errors.push(`${prefix}: invalid or duplicate slug`);
      ids.add(product.id);
      slugs.add(product.slug);
      if (!Array.isArray(product.gallery) || !product.gallery.length)
        errors.push(`${prefix}: gallery is empty`);
      if (!Number.isSafeInteger(product.priceCents) || product.priceCents < 0)
        errors.push(`${prefix}: price must be a non-negative integer in cents`);
      if (
        !product.isRevealed &&
        product.gallery.some((media) => media.isProductPhotography)
      ) {
        errors.push(`${prefix}: unrevealed product claims product photography`);
      }
    });
    return errors;
  }

  function renderAll() {
    const nativeHome = Boolean(
      document.querySelector("[data-product-grid] > [data-product-card]"),
    );
    if (!nativeHome) {
      renderStoreTokens();
      renderHero();
      renderProductNav();
      renderCollection();
      renderRealImageRail();
    }
    renderSpotlight();
    renderAnnouncement();
    renderMarquees();
    renderFilters();
    renderKit();
    if (document.querySelector("[data-pdp]")) renderPdp();
    renderCart();
    initAccordions();
    setupRevealElements();
    setupMagneticElements();
  }

  function init() {
    const dataErrors = validateCatalog();
    if (dataErrors.length) console.error("Catalog validation failed", dataErrors);
    if (document.querySelector("[data-hero]")) {
      const initialHero = heroProducts()[0];
      preloadMedia(homepageHeroMedia(initialHero));
    }
    document.addEventListener("click", handleDocumentClick);
    document.addEventListener("submit", handleDocumentSubmit);
    document.addEventListener("change", handleDocumentChange);
    document.addEventListener(
      "error",
      (event) => handleImageFailure(event.target),
      true,
    );
    document.querySelectorAll("dialog").forEach(bindDialogLifecycle);
    initMobileMenu();
    renderAll();
    initNativeDynamicCheckout();
    refreshCart();
    startHeroRotation();
    initHeroSwipe();
    initHeroParallax();
    initScrollProgress();

    document.querySelectorAll("[data-hero]").forEach((hero) => {
      hero.addEventListener("pointerenter", stopHeroRotation);
      hero.addEventListener("pointerleave", startHeroRotation);
      hero.addEventListener("focusin", stopHeroRotation);
      hero.addEventListener("focusout", (event) => {
        if (!hero.contains(event.relatedTarget)) startHeroRotation();
      });
    });

    document.addEventListener("visibilitychange", () => {
      if (document.hidden) stopHeroRotation();
      else startHeroRotation();
    });

    reducedMotionQuery.addEventListener?.("change", () => {
      document.documentElement.classList.toggle(
        "motion-ready",
        !reducedMotionQuery.matches,
      );
      if (reducedMotionQuery.matches) {
        stopHeroRotation();
        document
          .querySelectorAll("[data-reveal], .reveal")
          .forEach((element) => element.classList.add("is-visible"));
      } else {
        startHeroRotation();
        setupRevealElements();
      }
    });
  }

  window.PRESSD_APP = Object.freeze({
    store,
    formatMoney,
    getProduct,
    filterProducts,
    getRelated,
    chooseSpotlight,
    addToCart,
    setCartQuantity,
    cartCount,
    cartSubtotal,
    validateCatalog,
    renderAll,
    renderPdp,
    getCart: () => ({
      ...state.cart,
      items: (state.cart.items || []).map((item) => ({ ...item })),
    }),
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }
})();

